import React, { useEffect, useState, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { auth, db } from '../firebase';
import { onAuthStateChanged } from 'firebase/auth';
import {
  collection,
  getDocs,
  query,
  where,
  doc,
  deleteDoc,
  updateDoc,
  serverTimestamp,
} from 'firebase/firestore';

const styles = {
  page: {
    minHeight: '100vh',
    backgroundColor: '#F7F5F0',
    padding: '2rem',
  },
  header: {
    maxWidth: '1200px',
    margin: '0 auto 2rem',
    padding: '2.5rem',
    borderRadius: '16px',
    backgroundColor: '#FFFFFF',
    boxShadow: '0 18px 60px rgba(17, 17, 16, 0.08)',
  },
  headerTop: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: '1.5rem',
    flexWrap: 'wrap',
    gap: '1rem',
  },
  title: {
    margin: 0,
    fontFamily: 'Playfair Display, Georgia, serif',
    fontSize: '2.5rem',
    color: '#111110',
  },
  button: {
    padding: '12px 22px',
    borderRadius: '8px',
    border: 'none',
    cursor: 'pointer',
    fontFamily: 'DM Sans, system-ui, sans-serif',
    fontSize: '14px',
    textDecoration: 'none',
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  primaryButton: {
    backgroundColor: '#4A9E8A',
    color: '#FFFFFF',
  },
  secondaryButton: {
    backgroundColor: '#FFFFFF',
    color: '#111110',
    border: '1px solid #E0DDD6',
  },
  subtitle: {
    marginTop: '0.5rem',
    fontFamily: 'DM Sans, system-ui, sans-serif',
    fontSize: '1rem',
    color: '#555553',
    lineHeight: 1.75,
  },
  stats: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))',
    gap: '1rem',
    marginTop: '1.5rem',
  },
  statBox: {
    padding: '1rem',
    borderRadius: '8px',
    backgroundColor: '#F7F5F0',
    border: '1px solid #E0DDD6',
  },
  statLabel: {
    fontFamily: 'DM Sans, system-ui, sans-serif',
    fontSize: '12px',
    color: '#888880',
    textTransform: 'uppercase',
    letterSpacing: '0.05em',
    margin: 0,
  },
  statValue: {
    fontFamily: 'Playfair Display, Georgia, serif',
    fontSize: '28px',
    color: '#111110',
    margin: '0.5rem 0 0',
  },
  controls: {
    maxWidth: '1200px',
    margin: '0 auto 2rem',
    padding: '1.5rem',
    borderRadius: '16px',
    backgroundColor: '#FFFFFF',
    boxShadow: '0 4px 12px rgba(17, 17, 16, 0.04)',
  },
  controlsRow: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
    gap: '1rem',
    marginBottom: '1rem',
  },
  controlsRowLast: {
    marginBottom: 0,
  },
  label: {
    display: 'block',
    fontFamily: 'DM Sans, system-ui, sans-serif',
    fontSize: '13px',
    color: '#444441',
    marginBottom: '0.4rem',
  },
  input: {
    width: '100%',
    padding: '10px 12px',
    border: '1px solid #E0DDD6',
    borderRadius: '4px',
    fontFamily: 'DM Sans, system-ui, sans-serif',
    fontSize: '14px',
    color: '#111110',
    backgroundColor: '#FFFFFF',
    outline: 'none',
    boxSizing: 'border-box',
  },
  select: {
    width: '100%',
    padding: '10px 12px',
    border: '1px solid #E0DDD6',
    borderRadius: '4px',
    fontFamily: 'DM Sans, system-ui, sans-serif',
    fontSize: '14px',
    color: '#111110',
    backgroundColor: '#FFFFFF',
    outline: 'none',
    boxSizing: 'border-box',
  },
  clearBtn: {
    padding: '10px 16px',
    borderRadius: '4px',
    border: '1px solid #E0DDD6',
    backgroundColor: '#FFFFFF',
    color: '#111110',
    fontFamily: 'DM Sans, system-ui, sans-serif',
    fontSize: '13px',
    cursor: 'pointer',
  },
  tableWrapper: {
    maxWidth: '1200px',
    margin: '0 auto 2rem',
    overflowX: 'auto',
  },
  table: {
    width: '100%',
    borderCollapse: 'collapse',
    backgroundColor: '#FFFFFF',
    borderRadius: '8px',
    border: '1px solid #E0DDD6',
    overflow: 'hidden',
  },
  thead: {
    backgroundColor: '#F7F5F0',
    borderBottom: '1px solid #E0DDD6',
  },
  th: {
    padding: '1rem',
    fontFamily: 'DM Sans, system-ui, sans-serif',
    fontSize: '12px',
    fontWeight: 600,
    color: '#444441',
    textAlign: 'left',
    textTransform: 'uppercase',
    letterSpacing: '0.05em',
  },
  tbody: {},
  tr: {
    borderBottom: '1px solid #E0DDD6',
    transition: 'background-color 0.2s',
  },
  trHover: {
    backgroundColor: '#F9F8F6',
  },
  td: {
    padding: '1rem',
    fontFamily: 'DM Sans, system-ui, sans-serif',
    fontSize: '14px',
    color: '#111110',
  },
  thumbnail: {
    width: '75px',
    height: '75px',
    borderRadius: '6px',
    objectFit: 'cover',
    backgroundColor: '#EDEBE7',
  },
  statusBadge: {
    display: 'inline-block',
    padding: '6px 12px',
    borderRadius: '999px',
    fontFamily: 'DM Sans, system-ui, sans-serif',
    fontSize: '12px',
    fontWeight: 600,
    textTransform: 'capitalize',
    cursor: 'pointer',
    transition: 'all 0.2s',
  },
  statusActive: {
    backgroundColor: '#E8F5E9',
    color: '#2E7D32',
  },
  statusDraft: {
    backgroundColor: '#FFF3E0',
    color: '#E65100',
  },
  statusSold: {
    backgroundColor: '#F3E5F5',
    color: '#6A1B9A',
  },
  statusExpired: {
    backgroundColor: '#FFEBEE',
    color: '#C62828',
  },
  actionButtons: {
    display: 'flex',
    gap: '0.5rem',
  },
  editLink: {
    padding: '8px 12px',
    borderRadius: '6px',
    backgroundColor: '#4A9E8A',
    color: '#FFFFFF',
    textDecoration: 'none',
    fontFamily: 'DM Sans, system-ui, sans-serif',
    fontSize: '13px',
    cursor: 'pointer',
    border: 'none',
    display: 'inline-flex',
    alignItems: 'center',
  },
  deleteBtn: {
    padding: '8px 12px',
    borderRadius: '6px',
    backgroundColor: '#E24B4A',
    color: '#FFFFFF',
    fontFamily: 'DM Sans, system-ui, sans-serif',
    fontSize: '13px',
    cursor: 'pointer',
    border: 'none',
  },
  pagination: {
    maxWidth: '1200px',
    margin: '2rem auto',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    gap: '1rem',
    fontFamily: 'DM Sans, system-ui, sans-serif',
  },
  paginationBtn: {
    padding: '8px 12px',
    borderRadius: '6px',
    border: '1px solid #E0DDD6',
    backgroundColor: '#FFFFFF',
    color: '#111110',
    cursor: 'pointer',
    fontFamily: 'DM Sans, system-ui, sans-serif',
    fontSize: '13px',
  },
  paginationBtnActive: {
    backgroundColor: '#4A9E8A',
    color: '#FFFFFF',
    border: '1px solid #4A9E8A',
  },
  paginationBtnDisabled: {
    opacity: 0.5,
    cursor: 'not-allowed',
  },
  emptyState: {
    maxWidth: '1200px',
    margin: '2rem auto',
    padding: '3rem 2rem',
    borderRadius: '16px',
    backgroundColor: '#FFFFFF',
    border: '1px solid #E0DDD6',
    textAlign: 'center',
    fontFamily: 'DM Sans, system-ui, sans-serif',
  },
  emptyStateIcon: {
    fontSize: '48px',
    marginBottom: '1rem',
  },
  emptyStateText: {
    fontSize: '16px',
    color: '#555553',
    margin: '0 0 1.5rem',
  },
  error: {
    maxWidth: '1200px',
    margin: '1rem auto',
    fontFamily: 'DM Sans, system-ui, sans-serif',
    fontSize: '14px',
    color: '#E24B4A',
    textAlign: 'center',
    padding: '1rem',
    backgroundColor: '#FFEBEE',
    borderRadius: '8px',
    border: '1px solid #FFCDD2',
  },
  loading: {
    maxWidth: '1200px',
    margin: '2rem auto',
    fontFamily: 'DM Sans, system-ui, sans-serif',
    fontSize: '14px',
    color: '#555553',
    textAlign: 'center',
  },
  mobileCard: {
    maxWidth: '1200px',
    margin: '0 auto 1rem',
    padding: '1.5rem',
    backgroundColor: '#FFFFFF',
    borderRadius: '12px',
    border: '1px solid #E0DDD6',
    boxShadow: '0 2px 8px rgba(17, 17, 16, 0.04)',
  },
  mobileCardHeader: {
    display: 'flex',
    gap: '1rem',
    marginBottom: '1rem',
  },
  mobileCardThumbnail: {
    width: '80px',
    height: '80px',
    borderRadius: '8px',
    objectFit: 'cover',
    backgroundColor: '#EDEBE7',
    flexShrink: 0,
  },
  mobileCardInfo: {
    flex: 1,
  },
  mobileCardTitle: {
    margin: 0,
    fontFamily: 'Playfair Display, Georgia, serif',
    fontSize: '16px',
    color: '#111110',
    marginBottom: '0.5rem',
  },
  mobileCardMeta: {
    margin: '0.25rem 0',
    fontFamily: 'DM Sans, system-ui, sans-serif',
    fontSize: '13px',
    color: '#888880',
  },
  mobileCardActions: {
    display: 'flex',
    gap: '0.5rem',
    marginTop: '1rem',
  },
};

function Account() {
  const [user, setUser] = useState(null);
  const [listings, setListings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [authChecked, setAuthChecked] = useState(false);

  // Filter and sort state
  const [sortBy, setSortBy] = useState('date-newest');
  const [filterStatus, setFilterStatus] = useState('all');
  const [filterCategory, setFilterCategory] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');

  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);

  // Mobile detection
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, (currentUser) => {
      setUser(currentUser);
      setAuthChecked(true);
    });
    return () => unsubscribe();
  }, []);

  useEffect(() => {
    const fetchUserListings = async () => {
      if (!user) {
        return;
      }
      setLoading(true);
      setError('');
      try {
        const listingsRef = collection(db, 'listings');
        const q = query(listingsRef, where('userId', '==', user.uid));
        const snapshot = await getDocs(q);
        const userListings = snapshot.docs.map((doc) => ({ id: doc.id, ...doc.data() }));
        setListings(userListings);
      } catch (fetchError) {
        console.error('Account fetch error', fetchError);
        setError('Unable to load your listings. Please refresh the page.');
      } finally {
        setLoading(false);
      }
    };

    fetchUserListings();
  }, [user]);

  const handleDelete = async (listingId) => {
    if (!window.confirm('Are you sure you want to delete this listing? This action cannot be undone.')) {
      return;
    }
    setError('');
    try {
      await deleteDoc(doc(db, 'listings', listingId));
      setListings((prev) => prev.filter((listing) => listing.id !== listingId));
    } catch (deleteError) {
      console.error('Account delete error', deleteError);
      setError('Unable to delete the listing. Please try again.');
    }
  };

  const handleStatusChange = async (listingId, newStatus) => {
    try {
      await updateDoc(doc(db, 'listings', listingId), {
        status: newStatus,
        updatedAt: serverTimestamp(),
      });
      setListings((prev) =>
        prev.map((listing) =>
          listing.id === listingId ? { ...listing, status: newStatus } : listing
        )
      );
    } catch (err) {
      console.error('Error updating status:', err);
      setError('Unable to update listing status. Please try again.');
    }
  };

  // Calculate stats
  const stats = useMemo(() => {
    const total = listings.length;
    const active = listings.filter((l) => l.status === 'Active').length;
    const sold = listings.filter((l) => l.status === 'Sold').length;
    const expired = listings.filter((l) => l.status === 'Expired').length;
    return { total, active, sold, expired };
  }, [listings]);

  // Filter and sort listings
  const filteredAndSorted = useMemo(() => {
    let filtered = listings;

    // Apply search filter
    if (searchTerm) {
      filtered = filtered.filter((l) =>
        l.title?.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }

    // Apply status filter
    if (filterStatus !== 'all') {
      filtered = filtered.filter((l) => l.status === filterStatus);
    }

    // Apply category filter
    if (filterCategory !== 'all') {
      filtered = filtered.filter((l) => l.category === filterCategory);
    }

    // Apply sorting
    const sorted = [...filtered].sort((a, b) => {
      switch (sortBy) {
        case 'date-newest':
          return (b.createdAt?.toMillis?.() || 0) - (a.createdAt?.toMillis?.() || 0);
        case 'date-oldest':
          return (a.createdAt?.toMillis?.() || 0) - (b.createdAt?.toMillis?.() || 0);
        case 'price-low':
          return (a.price || 0) - (b.price || 0);
        case 'price-high':
          return (b.price || 0) - (a.price || 0);
        case 'status':
          return (a.status || 'Active').localeCompare(b.status || 'Active');
        default:
          return 0;
      }
    });

    return sorted;
  }, [listings, searchTerm, filterStatus, filterCategory, sortBy]);

  // Pagination
  const totalPages = Math.ceil(filteredAndSorted.length / pageSize);
  const paginatedListings = filteredAndSorted.slice(
    (currentPage - 1) * pageSize,
    currentPage * pageSize
  );

  const handleClearFilters = () => {
    setSearchTerm('');
    setFilterStatus('all');
    setFilterCategory('all');
    setSortBy('date-newest');
    setCurrentPage(1);
  };

  const getStatusBadgeStyle = (status) => {
    switch (status) {
      case 'Draft':
        return styles.statusDraft;
      case 'Sold':
        return styles.statusSold;
      case 'Expired':
        return styles.statusExpired;
      default:
        return styles.statusActive;
    }
  };

  const getThumbnailUrl = (listing) => {
    return listing.images?.[listing.thumbnailIndex || 0]?.thumbnail ||
           listing.images?.[0]?.thumbnail ||
           'https://via.placeholder.com/75x75?text=No+Image';
  };

  const formatDate = (timestamp) => {
    if (!timestamp) return 'N/A';
    const date = timestamp.toDate?.() || new Date(timestamp);
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  };

  if (authChecked && !user) {
    return (
      <main style={styles.page}>
        <div style={styles.header}>
          <h1 style={styles.title}>My Listings</h1>
          <p style={styles.subtitle}>Please log in to view and manage your listings.</p>
        </div>
        <div style={styles.emptyState}>
          <div style={styles.emptyStateIcon}>🔐</div>
          <p style={styles.emptyStateText}>You must be signed in to manage your listings.</p>
          <Link to="/login" style={{ ...styles.button, ...styles.primaryButton }}>
            Log in to your account
          </Link>
        </div>
      </main>
    );
  }

  return (
    <main style={styles.page}>
      <section style={styles.header}>
        <div style={styles.headerTop}>
          <div>
            <h1 style={styles.title}>My Listings</h1>
            <p style={styles.subtitle}>Manage all the listings you've posted on HostelChic.</p>
          </div>
          <Link to="/post" style={{ ...styles.button, ...styles.primaryButton }}>
            + Create New Listing
          </Link>
        </div>

        <div style={styles.stats}>
          <div style={styles.statBox}>
            <p style={styles.statLabel}>Total Listings</p>
            <p style={styles.statValue}>{stats.total}</p>
          </div>
          <div style={styles.statBox}>
            <p style={styles.statLabel}>Active</p>
            <p style={styles.statValue}>{stats.active}</p>
          </div>
          <div style={styles.statBox}>
            <p style={styles.statLabel}>Sold</p>
            <p style={styles.statValue}>{stats.sold}</p>
          </div>
          <div style={styles.statBox}>
            <p style={styles.statLabel}>Expired</p>
            <p style={styles.statValue}>{stats.expired}</p>
          </div>
        </div>
      </section>

      {error && <div style={styles.error}>{error}</div>}

      {loading ? (
        <p style={styles.loading}>Loading your listings…</p>
      ) : listings.length === 0 ? (
        <div style={styles.emptyState}>
          <div style={styles.emptyStateIcon}>📋</div>
          <p style={styles.emptyStateText}>You haven't posted any listings yet.</p>
          <Link to="/post" style={{ ...styles.button, ...styles.primaryButton }}>
            Create Your First Listing
          </Link>
        </div>
      ) : (
        <>
          <section style={styles.controls}>
            <div style={styles.controlsRow}>
              <div>
                <label style={styles.label}>Search by title</label>
                <input
                  style={styles.input}
                  type="text"
                  placeholder="Search listings..."
                  value={searchTerm}
                  onChange={(e) => {
                    setSearchTerm(e.target.value);
                    setCurrentPage(1);
                  }}
                />
              </div>
              <div>
                <label style={styles.label}>Sort by</label>
                <select
                  style={styles.select}
                  value={sortBy}
                  onChange={(e) => {
                    setSortBy(e.target.value);
                    setCurrentPage(1);
                  }}
                >
                  <option value="date-newest">Date Posted (Newest)</option>
                  <option value="date-oldest">Date Posted (Oldest)</option>
                  <option value="price-low">Price (Low to High)</option>
                  <option value="price-high">Price (High to Low)</option>
                  <option value="status">Status</option>
                </select>
              </div>
              <div>
                <label style={styles.label}>Filter by status</label>
                <select
                  style={styles.select}
                  value={filterStatus}
                  onChange={(e) => {
                    setFilterStatus(e.target.value);
                    setCurrentPage(1);
                  }}
                >
                  <option value="all">All Statuses</option>
                  <option value="Active">Active</option>
                  <option value="Draft">Draft</option>
                  <option value="Sold">Sold</option>
                  <option value="Expired">Expired</option>
                </select>
              </div>
              <div>
                <label style={styles.label}>Filter by category</label>
                <select
                  style={styles.select}
                  value={filterCategory}
                  onChange={(e) => {
                    setFilterCategory(e.target.value);
                    setCurrentPage(1);
                  }}
                >
                  <option value="all">All Categories</option>
                  <option value="stays">Stays</option>
                  <option value="marketplace">Marketplace</option>
                </select>
              </div>
            </div>
            <div style={styles.controlsRow}>
              <div>
                <label style={styles.label}>Items per page</label>
                <select
                  style={styles.select}
                  value={pageSize}
                  onChange={(e) => {
                    setPageSize(Number(e.target.value));
                    setCurrentPage(1);
                  }}
                >
                  <option value={10}>10 per page</option>
                  <option value={15}>15 per page</option>
                  <option value={25}>25 per page</option>
                </select>
              </div>
              <div style={{ display: 'flex', alignItems: 'flex-end' }}>
                <button style={styles.clearBtn} onClick={handleClearFilters}>
                  Clear Filters
                </button>
              </div>
            </div>
          </section>

          {filteredAndSorted.length === 0 ? (
            <div style={styles.emptyState}>
              <div style={styles.emptyStateIcon}>🔍</div>
              <p style={styles.emptyStateText}>No listings match your filters.</p>
              <button style={styles.clearBtn} onClick={handleClearFilters}>
                Clear Filters
              </button>
            </div>
          ) : (
            <>
              {!isMobile && (
                <div style={styles.tableWrapper}>
                  <table style={styles.table}>
                    <thead style={styles.thead}>
                      <tr>
                        <th style={styles.th}>Thumbnail</th>
                        <th style={styles.th}>Title</th>
                        <th style={styles.th}>Price</th>
                        <th style={styles.th}>Category</th>
                        <th style={styles.th}>Status</th>
                        <th style={styles.th}>Date Posted</th>
                        <th style={styles.th}>Last Updated</th>
                        <th style={styles.th}>Views</th>
                        <th style={styles.th}>Actions</th>
                      </tr>
                    </thead>
                    <tbody style={styles.tbody}>
                      {paginatedListings.map((listing) => (
                        <tr
                          key={listing.id}
                          style={styles.tr}
                          onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = styles.trHover.backgroundColor)}
                          onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = 'transparent')}
                        >
                          <td style={styles.td}>
                            <img
                              src={getThumbnailUrl(listing)}
                              alt={listing.title}
                              style={styles.thumbnail}
                              loading="lazy"
                            />
                          </td>
                          <td style={styles.td}>
                            <Link to={`/listing/${listing.id}`} style={{ color: '#4A9E8A', textDecoration: 'none' }}>
                              {listing.title}
                            </Link>
                          </td>
                          <td style={styles.td}>${listing.price?.toFixed(2) || '0.00'}</td>
                          <td style={styles.td}>{listing.category === 'stays' ? 'Stays' : 'Marketplace'}</td>
                          <td style={styles.td}>
                            <select
                              value={listing.status || 'Active'}
                              onChange={(e) => handleStatusChange(listing.id, e.target.value)}
                              style={{
                                ...styles.statusBadge,
                                ...getStatusBadgeStyle(listing.status),
                                cursor: 'pointer',
                                border: 'none',
                                padding: '6px 8px',
                              }}
                            >
                              <option value="Draft">Draft</option>
                              <option value="Active">Active</option>
                              <option value="Sold">Sold</option>
                              <option value="Expired">Expired</option>
                            </select>
                          </td>
                          <td style={styles.td}>{formatDate(listing.createdAt)}</td>
                          <td style={styles.td}>{formatDate(listing.updatedAt)}</td>
                          <td style={styles.td}>{listing.viewCount || 0}</td>
                          <td style={styles.td}>
                            <div style={styles.actionButtons}>
                              <Link
                                to={`/post/${listing.id}`}
                                style={styles.editLink}
                              >
                                Edit
                              </Link>
                              <button
                                style={styles.deleteBtn}
                                onClick={() => handleDelete(listing.id)}
                              >
                                Delete
                              </button>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}

              {isMobile && (
                <div>
                  {paginatedListings.map((listing) => (
                    <div key={listing.id} style={styles.mobileCard}>
                      <div style={styles.mobileCardHeader}>
                        <img
                          src={getThumbnailUrl(listing)}
                          alt={listing.title}
                          style={styles.mobileCardThumbnail}
                          loading="lazy"
                        />
                        <div style={styles.mobileCardInfo}>
                          <h3 style={styles.mobileCardTitle}>{listing.title}</h3>
                          <p style={styles.mobileCardMeta}>${listing.price?.toFixed(2) || '0.00'}</p>
                          <p style={styles.mobileCardMeta}>{listing.category === 'stays' ? 'Stays' : 'Marketplace'}</p>
                          <p style={styles.mobileCardMeta}>{listing.viewCount || 0} views</p>
                        </div>
                      </div>
                      <div style={{ marginBottom: '1rem' }}>
                        <select
                          value={listing.status || 'Active'}
                          onChange={(e) => handleStatusChange(listing.id, e.target.value)}
                          style={{
                            width: '100%',
                            ...styles.statusBadge,
                            ...getStatusBadgeStyle(listing.status),
                            cursor: 'pointer',
                            border: '1px solid #CCC',
                            padding: '8px',
                            textAlign: 'center',
                          }}
                        >
                          <option value="Draft">Draft</option>
                          <option value="Active">Active</option>
                          <option value="Sold">Sold</option>
                          <option value="Expired">Expired</option>
                        </select>
                      </div>
                      <div style={styles.mobileCardActions}>
                        <Link
                          to={`/post/${listing.id}`}
                          style={{ ...styles.editLink, flex: 1, justifyContent: 'center' }}
                        >
                          Edit
                        </Link>
                        <button
                          style={{ ...styles.deleteBtn, flex: 1 }}
                          onClick={() => handleDelete(listing.id)}
                        >
                          Delete
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              )}

              {totalPages > 1 && (
                <div style={styles.pagination}>
                  <button
                    style={{
                      ...styles.paginationBtn,
                      ...(currentPage === 1 ? styles.paginationBtnDisabled : {}),
                    }}
                    onClick={() => setCurrentPage(currentPage - 1)}
                    disabled={currentPage === 1}
                  >
                    ← Previous
                  </button>

                  {Array.from({ length: totalPages }, (_, i) => i + 1)
                    .slice(Math.max(0, currentPage - 2), Math.min(totalPages, currentPage + 2))
                    .map((page) => (
                      <button
                        key={page}
                        style={{
                          ...styles.paginationBtn,
                          ...(currentPage === page ? styles.paginationBtnActive : {}),
                        }}
                        onClick={() => setCurrentPage(page)}
                      >
                        {page}
                      </button>
                    ))}

                  <button
                    style={{
                      ...styles.paginationBtn,
                      ...(currentPage === totalPages ? styles.paginationBtnDisabled : {}),
                    }}
                    onClick={() => setCurrentPage(currentPage + 1)}
                    disabled={currentPage === totalPages}
                  >
                    Next →
                  </button>
                </div>
              )}

              <div style={{ ...styles.loading, marginBottom: '2rem' }}>
                Showing {paginatedListings.length > 0 ? (currentPage - 1) * pageSize + 1 : 0} -{' '}
                {Math.min(currentPage * pageSize, filteredAndSorted.length)} of {filteredAndSorted.length} listings
              </div>
            </>
          )}
        </>
      )}
    </main>
  );
}

export default Account;
